import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ImageDataLike } from 'gatsby-plugin-image';
import React, { FC } from 'react';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
import { createMarkup, getBackgroundColor } from '../../utils';
import { Divider } from '../divider/Divider';
import { Image } from '../Image/Image';
import { LinkLabel } from '../linkLabel';
import { Spacer } from '../spacer';
import { Tag } from '../tag';
import { Typography, TypographyProps } from '../typography';
import { TickList } from '../tickList/TickList';
import { HtmlContent } from '../htmlContent/HtmlContent';
import { Button } from '../button';
// Interface

export interface CardProps {
  as?: any;
  background?: 'none' | 'white';
  image?: ImageDataLike | string;
  tag?: string;
  heading?: string;
  description?: string;
  metaTitle?: string;
  metaDetail?: string;
  url?: string;
  label?: string;
  onClick?: any;
  list?: { label: string }[];
}

export interface CardWrapperProps {
  url?: string;
  onClick?: any;
  rest?: any;
}

interface StyledCardProps {
  isClickable?: boolean;
  hasTickList?: boolean;
}

interface StyledBodyProps {
  hasImage?: boolean;
  background?: ThemeColorsKeys | 'none';
}

interface StyledImageProps {
  hasBackground?: boolean;
}

// Styles

const StyledCard = styled.div<StyledCardProps>`
  ${({ theme: { media, space, colors }, horizontal, hasTickList }) => css`
    overflow: hidden;
    width: 100%;
    display: flex;
    flex-direction: column;
    position: relative;

    img {
      /* aspect-ratio: 4 / 3; */
      width: 100%;
    }

    a {
      position: unset;

      &:before {
        content: '';
        position: absolute;
        display: block;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }
    }
  `};
`;

const StyledBody = styled(Spacer)<StyledBodyProps>`
  ${({ theme: { space, colors }, background }) => css`
    flex-grow: 1;
    display: flex;
    flex-direction: column;

    ${background !== 'none' &&
    css`
      ${getBackgroundColor('neutralWhite')};
      border: solid 1px ${colors.neutralMidGrey};
      padding: ${space.small};
    `}
  `};
`;

const StyledImage = styled.div<StyledImageProps>`
  ${({ theme: { space }, hasBackground }) => css`
    margin-bottom: ${hasBackground ? 0 : space.small};
  `}
`;

const StyledCardContent = styled(Spacer)`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

// JSX

export const Card: FC<React.PropsWithChildren<CardProps>> = ({
  as,
  background = 'white',
  image,
  tag,
  heading,
  description,
  url,
  metaTitle,
  metaDetail,
  label,
  onClick,
  children,
  list,
  isFirst,
  ...rest
}) => {
  const handleClick = onClick;

  // Calculate background colour
  let backgroundColour: ThemeColorsKeys | 'none';

  switch (background) {
    case 'white':
      backgroundColour = 'neutralWhite';
      break;
    default:
      backgroundColour = 'none';
  }

  // Image

  return (
    <StyledCard isClickable={!!url} hasTickList={!!list} {...rest}>
      {image && (
        <StyledImage hasBackground={backgroundColour !== 'none'}>
          <Image src={image} alt={heading} />
        </StyledImage>
      )}

      <StyledBody background={backgroundColour} spacing="small">
        <>
          <StyledCardContent spacing="small">
            {tag && <Tag label={tag} />}
            {heading && (
              <>
                <Typography component="h3" variant="headingMediumBold">
                  {createMarkup(heading)}
                  <br />
                  {/* I don't know why this works but it makes break tags in the title work. */}
                </Typography>
                {list && list.length > 0 && <Divider />}
              </>
            )}
            {metaTitle || metaDetail ? (
              <Typography component="p" variant="bodyMedium">
                {metaTitle && (
                  <>
                    <strong>{metaTitle}</strong>
                    <br />
                  </>
                )}
                {metaDetail && <>{metaDetail}</>}
              </Typography>
            ) : (
              ''
            )}
            {description && <HtmlContent>{description}</HtmlContent>}

            {list && list.length > 0 && <TickList items={list} />}
          </StyledCardContent>

          {url && (
            <Spacer>
              {isFirst ? (
                <Button label={label} url={url} onClick={handleClick} />
              ) : (
                <LinkLabel label={label} url={url} onClick={handleClick} />
              )}
            </Spacer>
          )}
        </>
      </StyledBody>
    </StyledCard>
  );
};

export default Card;
